<template>
  <StoryblokRichText :doc="document" :resolvers="resolvers" />
</template>

<script setup lang="ts">
import type { StoryblokRichTextNode } from '@storyblok/vue'
import type { RichtextStoryblok } from '~/types/storyblok'
import type { InlineComponent } from '~/types/inline'
import {
  InlineAccordion,
  InlineCheckbox,
  InlineCitation,
  InlineHeadingIcon,
  InlineImageLink,
  InlineImageLockup,
  InlineNewsletterCta,
  InlineRelatedContent,
  InlineVideo,
  NuxtLink,
  RichTextHeading,
  RichTextImage,
} from '#components'

defineProps<{
  document: RichtextStoryblok
}>()

// Resolve custom RichText components
const resolvers = {
  [MarkTypes.LINK]: (node: StoryblokRichTextNode<VNode>) => {
    const { href, text, ...attrs } = node.attrs
    const to =
      attrs.linktype === 'email' && !href.startsWith('mailto:')
        ? `mailto:${href}`
        : href
    return h(NuxtLink, { to, ...attrs }, () => node.text)
  },
  [BlockTypes.HEADING]: (node: StoryblokRichTextNode<VNode>) =>
    h(
      RichTextHeading,
      {
        level: `${node.attrs.level}`,
      },
      () => node.children,
    ),
  [BlockTypes.IMAGE]: (node: StoryblokRichTextNode<VNode>) =>
    h(
      RichTextImage,
      {
        filename: node.attrs.src,
        alt: node.attrs.alt,
        title: node.attrs.title,
      },
      () => node.children,
    ),
  [BlockTypes.COMPONENT]: (node: StoryblokRichTextNode<VNode>) => {
    return node.attrs.body.map((blok: InlineComponent) => {
      // Note - we can't simply resolve the component here because of the way Storyblok may dynamically change the component in the editor
      // so we need to manually map all the possible inline component names to the correct vue component here
      let component
      switch (blok.component) {
        case 'InlineCheckbox':
          component = InlineCheckbox
          break
        case 'InlineAccordion':
          component = InlineAccordion
          break
        case 'InlineCitation':
          component = InlineCitation
          break
        case 'InlineHeadingIcon':
          component = InlineHeadingIcon
          break
        case 'InlineImageLink':
          component = InlineImageLink
          break
        case 'InlineImageLockup':
          component = InlineImageLockup
          break
        case 'InlineNewsletterCta':
          component = InlineNewsletterCta
          break
        case 'InlineRelatedContent':
          component = InlineRelatedContent
          break
        case 'InlineVideo':
          component = InlineVideo
          break
        default:
          break
      }
      if (component) {
        return h(component, { blok: blok }, () => blok.content)
      } else {
        console.error(
          '!!RichTextRenderer: unable to resolve: blok.component',
          blok,
        )
        return null
      }
    })
  },
}
</script>
